import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronUp } from "lucide-react";
import ComboIcon from "./ComboIcon";
import FormulationTable from "./FormulationTable";

export const ComboCard = ({ combo, ingredients = [], basisLabel = "per tablet", index = 0 }) => {
    const [open, setOpen] = useState(false);
    const preview = ingredients.slice(0, 7);
    const accent = combo.color || "#7CB342";

    return (
        <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            data-testid={`combo-card-${combo.id}`}
            className="relative rounded-sm border bg-bg1 transition-colors"
            style={{ borderColor: open ? `${accent}66` : "rgba(255,255,255,0.10)",
                     backgroundColor: open ? "rgba(255,255,255,0.03)" : undefined }}
        >
            <div className="p-7">
                <div className="flex items-start justify-between gap-4">
                    <div className="mono text-[11px] tracking-[0.25em] text-zinc-600">{combo.number}</div>
                    <div className="w-14 h-14 rounded-sm flex items-center justify-center shrink-0"
                        style={{ background: `${accent}1f`, border: `1px solid ${accent}55` }}>
                        <ComboIcon icon={combo.icon} url={combo.icon_url} color={accent} className="w-8 h-8" />
                    </div>
                </div>

                <h3 className="mt-5 font-display text-2xl uppercase tracking-tight">{combo.name}</h3>
                {combo.support_label && (
                    <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em]" style={{ color: accent }}>{combo.support_label}</div>
                )}
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{combo.description}</p>

                {!!preview.length && (
                    <div className="mt-5">
                        <div className="mono text-[9px] tracking-[0.22em] text-zinc-500 mb-2.5">KEY ACTIVES</div>
                        <div className="flex flex-wrap gap-1.5">
                            {preview.map((ing) => (
                                <span key={ing.id || ing.name}
                                    className="text-[11px] text-zinc-300 border border-white/10 px-2.5 py-1 rounded-full">
                                    {ing.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <button
                    onClick={() => setOpen(!open)}
                    data-testid={`combo-explore-${combo.id}`}
                    className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold hover:text-white transition-colors"
                    style={{ color: accent }}
                >
                    {open ? <>Close <ChevronUp className="w-3.5 h-3.5" /></> : <>Explore Combo <ArrowRight className="w-3.5 h-3.5" /></>}
                </button>
            </div>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-white/10"
                        data-testid={`combo-detail-${combo.id}`}
                    >
                        <div className="p-7 pt-6">
                            <div className="mono text-[9px] tracking-[0.22em] text-zinc-500 mb-4">
                                INGREDIENTS {basisLabel.toUpperCase()}
                            </div>
                            <FormulationTable ingredients={ingredients} basisLabel={basisLabel} compact />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default ComboCard;
